import React from "react";
import { Box, FormControl, InputLabel, Select, MenuItem, TextField } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import PaletteIcon from "@material-ui/icons/Palette";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import ErEditAccordion from "./ErEditAccordion";
import TextEffectPicker from "./TextEffectPicker";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import { DraftIntTextField } from "./escapeRoomEditorFloatingWidgets";
import { pickEffectBundle, mergeEffectBundlePatch } from "./textEffectStyles";
import { normalizeColorToRgba } from "./escapeRoomEditorQuizVisual";
import { ADD_FONT_OPTION } from "./escapeRoomEditorUiConstants";
import { SCORE_TEXT_TOKEN, isScoreTextElement } from "./escapeRoomScoreText";

export default function TextElementStylePanel({
	selectedElement,
	effectiveUpdateElement,
	allFontOptions,
	fontLabel,
	onOpenFontManager,
}) {
	const el = selectedElement;
	const style = el.style || {};

	return (
		<ErEditAccordion title="Estilo del texto" icon={TextFieldsIcon} defaultOpen>
			{isScoreTextElement(el.content) ? (
				<Box p={1.5} mb={1.5} borderRadius={10} style={{ background: "#faf5ff", border: "1px solid #ddd6fe" }}>
					<Box fontSize={12} fontWeight={600} color="#6d28d9" mb={0.5}>Texto de puntuación</Box>
					<Box fontSize={11} color="#64748b" lineHeight={1.45}>
						En el lienzo verás la puntuación en vivo (0 en el editor). Al editar, usa <Box component="span" fontFamily="monospace" fontWeight={700}>{SCORE_TEXT_TOKEN}</Box> donde quieras el número.
					</Box>
				</Box>
			) : null}
			<ErEditAccordion title="Tipografía" nested defaultOpen>
				<Box display="flex" style={{ gap: 12, marginBottom: 10 }}>
					<FormControl size="small" style={{ flex: 1, minWidth: 0 }} variant="outlined">
						<InputLabel>Fuente</InputLabel>
						<Select
							value={style.fontFamily || "Arial, sans-serif"}
							onChange={(e) => {
								const v = e.target.value;
								if (v === ADD_FONT_OPTION) {
									onOpenFontManager();
									return;
								}
								effectiveUpdateElement({ ...el, style: { ...style, fontFamily: v } });
							}}
							label="Fuente"
						>
							{allFontOptions.map((f) => (
								<MenuItem key={f} value={f} style={{ fontFamily: f }}>
									{fontLabel(f)}
								</MenuItem>
							))}
							<MenuItem value={ADD_FONT_OPTION}>+ Añadir fuente</MenuItem>
						</Select>
					</FormControl>
					<DraftIntTextField
						label="Tamaño"
						min={8}
						max={500}
						style={{ flex: 1, minWidth: 72 }}
						value={style.fontSize ?? 18}
						onCommit={(n) => effectiveUpdateElement({ ...el, style: { ...style, fontSize: n } })}
					/>
				</Box>
				<Box display="flex" style={{ gap: 12 }}>
					<TextField
						type="number"
						size="small"
						variant="outlined"
						label="Espaciado letras (px)"
						value={String(Number.isFinite(Number(style.letterSpacing)) ? Number(style.letterSpacing) : 0)}
						onChange={(e) => {
							const v = Number(e?.target?.value);
							if (!Number.isFinite(v)) return;
							effectiveUpdateElement({ ...el, style: { ...style, letterSpacing: Math.max(-10, Math.min(40, v)) } });
						}}
						inputProps={{ step: 0.1, min: -10, max: 40 }}
						style={{ flex: 1, minWidth: 0 }}
					/>
					<TextField
						type="number"
						size="small"
						variant="outlined"
						label="Espaciado líneas"
						value={String(Number.isFinite(Number(style.lineHeight)) ? Number(style.lineHeight) : 1.3)}
						onChange={(e) => {
							const v = Number(e?.target?.value);
							if (!Number.isFinite(v)) return;
							effectiveUpdateElement({ ...el, style: { ...style, lineHeight: Math.max(0.8, Math.min(3, v)) } });
						}}
						inputProps={{ step: 0.05, min: 0.8, max: 3 }}
						style={{ flex: 1, minWidth: 0 }}
					/>
				</Box>
			</ErEditAccordion>

			<ErEditAccordion title="Colores" nested defaultOpen>
				<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
					<Box display="flex" alignItems="center" style={{ gap: 12, padding: "10px 12px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
						<Box fontSize={12} fontWeight={500} color="#334155" style={{ minWidth: 48 }}>Texto</Box>
						<RgbaColorPickerPopover
							label=""
							value={normalizeColorToRgba(style.color)}
							onChange={(c) => effectiveUpdateElement({ ...el, style: { ...style, color: c } })}
						/>
					</Box>
					<Box display="flex" alignItems="center" style={{ gap: 12, padding: "10px 12px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
						<Box fontSize={12} fontWeight={500} color="#334155" style={{ minWidth: 48 }}>Fondo</Box>
						<RgbaColorPickerPopover
							label=""
							value={normalizeColorToRgba(style.backgroundColor, 0)}
							onChange={(c) => effectiveUpdateElement({ ...el, style: { ...style, backgroundColor: c } })}
						/>
					</Box>
				</Box>
			</ErEditAccordion>

			<ErEditAccordion title="Estilo y alineación" nested defaultOpen>
				<Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 14 }}>
					<ToggleButtonGroup
						size="small"
						exclusive={false}
						value={[
							(style.fontWeight === "bold" && "bold") || null,
							(style.fontStyle === "italic" && "italic") || null,
							(style.textDecoration === "underline" && "underline") || null,
						].filter(Boolean)}
						onChange={(_, val) => {
							if (!val) return;
							const st = { ...style };
							st.fontWeight = val.includes("bold") ? "bold" : "normal";
							st.fontStyle = val.includes("italic") ? "italic" : "normal";
							st.textDecoration = val.includes("underline") ? "underline" : "none";
							effectiveUpdateElement({ ...el, style: st });
						}}
					>
						<ToggleButton value="bold" style={{ minWidth: 44, minHeight: 40 }}><FormatBoldIcon style={{ fontSize: 18 }} /></ToggleButton>
						<ToggleButton value="italic" style={{ minWidth: 44, minHeight: 40 }}><FormatItalicIcon style={{ fontSize: 18 }} /></ToggleButton>
						<ToggleButton value="underline" style={{ minWidth: 44, minHeight: 40 }}><FormatUnderlinedIcon style={{ fontSize: 18 }} /></ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={style.textAlign || "left"}
						onChange={(_, val) => { if (val != null) effectiveUpdateElement({ ...el, style: { ...style, textAlign: val } }); }}
					>
						<ToggleButton value="left" style={{ minWidth: 44, minHeight: 40 }}><FormatAlignLeftIcon style={{ fontSize: 18 }} /></ToggleButton>
						<ToggleButton value="center" style={{ minWidth: 44, minHeight: 40 }}><FormatAlignCenterIcon style={{ fontSize: 18 }} /></ToggleButton>
						<ToggleButton value="right" style={{ minWidth: 44, minHeight: 40 }}><FormatAlignRightIcon style={{ fontSize: 18 }} /></ToggleButton>
						<ToggleButton value="justify" style={{ minWidth: 44, minHeight: 40 }}><FormatAlignJustifyIcon style={{ fontSize: 18 }} /></ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</ErEditAccordion>

			<ErEditAccordion title="Efecto visual" nested>
				<TextEffectPicker
					embedded
					effectStyle={pickEffectBundle(style, "text")}
					fallbackColor={style.color}
					onEffectStyleChange={(patch) =>
						effectiveUpdateElement({
							...el,
							style: mergeEffectBundlePatch(style, "text", patch),
						})
					}
				/>
			</ErEditAccordion>
		</ErEditAccordion>
	);
}
